import type {ComponentPropsWithoutRef} from 'react';
import {forwardRef} from 'react';
import {Slot} from '@radix-ui/react-slot';
import type {VariantProps} from 'class-variance-authority';
import {cva} from 'class-variance-authority';
import {twMerge} from 'tailwind-merge';

const button = cva(
	[
		'no-underline',
		'inline-flex',
		'items-center',
		'justify-center',
		'gap-2',
		'shrink-0',
		'transition-colors',
		'rounded-full',
	],
	{
		variants: {
			intent: {
				primary: [
					'bg-primary',
					'hover:bg-[#009BCF]',
					'text-white',
					'active:text-[#BFD2DF]',
					'active:bg-primary',
				],
				secondary: [
					'bg-white',
					'text-[#014A7F]',
					'hover:bg-secondary-dark',
					'hover:text-white',
					'active:bg-secondary-light',
					'active:text-[#014A7F]',
					'border',
					'border-primary-content',
				],
			},
			size: {
				small: ['px-4', 'py-2', 'text-sm', 'shadow-[0_2px_4px_0px_rgba(0,74,127,0.25)]'],
				medium: ['px-5', 'py-3', 'text-base', 'shadow-[0_3px_4px_0px_rgba(0,74,127,0.25)]'],
				large: ['px-8', 'py-4', 'text-xl', 'shadow-[0_4px_4px_0px_rgba(0,74,127,0.25)]'],
			},
		},
		defaultVariants: {
			intent: 'primary',
			size: 'medium',
		},
	},
);

export type ButtonProps = VariantProps<typeof button> &
	ComponentPropsWithoutRef<'button'> & {
		/**
		 * Change the default rendered element for the one passed as a child, merging their props and behavior.
		 * @default false
		 */
		asChild?: boolean;
	};

/**
 * Button used for interaction
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			children,
			intent = 'primary',
			size = 'medium',
			type = 'button',
			className,
			asChild,
			...rest
		},
		ref,
	) => {
		const Component = asChild ? Slot : 'button';

		return (
			<Component
				ref={ref}
				type={type}
				className={twMerge(button({intent, size, className}))}
				{...rest}
			>
				{children}
			</Component>
		);
	},
);

Button.displayName = 'Button';
